import React, { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { setWordMastery, MasteryThree } from '../../redux/actions/wordsAction'
import { WordItem } from '../../redux/reducers/wordsReducer'
import { useWordAudio } from '../../hooks/useWordAudio'
import { AppUseSelector } from '../../redux/hooks'
import { renderConverterWord } from '../../utils/helperFunction'
import { useChineseLang } from '../../i18n/chinese'
import { formatDate, formatRelative } from '../../i18n/format'
import { wordMeaning } from '../../i18n/localizedContent'

// Labels are translation keys.
const masteryOptions: { value: MasteryThree; label: string }[] = [
  { value: 'unknown', label: 'words:mastery.unknown' },
  { value: 'learning', label: 'words:mastery.learning' },
  { value: 'mastered', label: 'words:mastery.known' },
]

// The full date and time behind "Seen 3 days ago".
const LAST_SEEN_TITLE: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
}

interface Props {
  item: WordItem
  selected?: boolean
  onToggleSelect?: () => void
}

const AudioButton: React.FC<{ item: WordItem; label: string }> = ({ item, label }) => {
  const { t } = useTranslation('words')
  const { playWordAudio, isPlaying } = useWordAudio()
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    void playWordAudio({ _id: item.wordId, has_audio: item.hasAudio })
  }
  return (
    <button
      type='button'
      className={`word-row-audio${isPlaying ? ' playing' : ''}`}
      onClick={onClick}
      aria-label={t('row.playPronunciationFor', { word: label })}
      title={t('row.playPronunciation')}
    >
      <svg width='14' height='14' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
        <path d='M11 5L6 9H2v6h4l5 4V5zm4.54 0.46a1 1 0 0 1 1.42 0 8 8 0 0 1 0 11.32 1 1 0 0 1-1.42-1.42 6 6 0 0 0 0-8.48 1 1 0 0 1 0-1.42zm-2.83 2.83a1 1 0 0 1 1.42 0 4 4 0 0 1 0 5.66 1 1 0 1 1-1.42-1.42 2 2 0 0 0 0-2.82 1 1 0 0 1 0-1.42z' />
      </svg>
    </button>
  )
}

const StruggleBadge: React.FC<{ lookups?: number }> = ({ lookups }) => {
  const { t } = useTranslation('words')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const handlePointer = (e: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('touchstart', handlePointer)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('touchstart', handlePointer)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  return (
    <span className='word-struggle-wrap' ref={wrapRef}>
      <button
        type='button'
        className='word-struggle-badge'
        onClick={() => setOpen((o) => !o)}
        aria-label={t('struggle.badgeLabel')}
        aria-expanded={open}
      >
        <svg
          className='word-struggle-icon'
          width='10'
          height='10'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2.2'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
          focusable='false'
        >
          <path d='M12 3 2 20h20L12 3z' />
          <line x1='12' y1='10' x2='12' y2='14' />
          <line x1='12' y1='17' x2='12.01' y2='17' />
        </svg>
        <span>
          {lookups && lookups > 1 ? t('struggle.badgeWithLookups', { count: lookups }) : t('struggle.badge')}
        </span>
      </button>
      {open && (
        <>
          <div className='words-info-popover-scrim' aria-hidden='true' onClick={() => setOpen(false)} />
          <div className='words-info-popover' role='dialog' aria-label={t('struggle.title')}>
            <div className='words-info-popover-title'>{t('struggle.title')}</div>
            <ul className='words-info-popover-list'>
              <li>
                {lookups && lookups > 0 ? (
                  <Trans
                    t={t}
                    i18nKey='struggle.lookedUp'
                    values={{ count: lookups }}
                    components={{ strong: <strong /> }}
                  />
                ) : (
                  <Trans t={t} i18nKey='struggle.lookedUpDefault' components={{ strong: <strong /> }} />
                )}
              </li>
              <li>
                <Trans t={t} i18nKey='struggle.notKnown' components={{ strong: <strong /> }} />
              </li>
            </ul>
            <div className='words-info-popover-tip'>{t('struggle.tip')}</div>
          </div>
        </>
      )}
    </span>
  )
}

const WordRow: React.FC<Props> = ({ item, selected, onToggleSelect }) => {
  const { t } = useTranslation('words')
  const zhLang = useChineseLang()
  const { credentials } = AppUseSelector(({ user }) => user)
  const characterPreference = credentials?.customFields?.character
  // Dictionary words are stored Simplified-only; convert client-side to honour
  // the learner's Traditional/Simplified setting, matching every other surface.
  const displayWord = renderConverterWord(item.word, characterPreference)

  const handleMastery = async (m: MasteryThree) => {
    if (m === item.mastery) return
    try {
      await setWordMastery(item.wordId, m)
    } catch {
      // revert handled inside the action
    }
  }

  const struggling = !!item.struggling
  const lastSeenLabel = item.lastSeenAt ? formatRelative(item.lastSeenAt) || null : null
  const lastSeenTitle = item.lastSeenAt ? formatDate(item.lastSeenAt, LAST_SEEN_TITLE) || undefined : undefined
  // The learner-language meaning when the API sent one, else the English.
  const meaning = wordMeaning(item, item.english).primary

  return (
    <div className={`word-row${selected ? ' selected' : ''}`}>
      {onToggleSelect && (
        <label className='word-row-select' onClick={(e) => e.stopPropagation()}>
          <input
            type='checkbox'
            checked={!!selected}
            onChange={onToggleSelect}
            aria-label={t('row.select', { word: displayWord })}
          />
        </label>
      )}
      <div className='word-row-info'>
        <div className='word-row-line'>
          <span className='word-row-hanzi' lang={zhLang}>
            {displayWord}
          </span>
          {item.hasAudio && <AudioButton item={item} label={displayWord} />}
          {item.pinyin && <span className='word-row-pinyin'>{item.pinyin}</span>}
        </div>
        {meaning.text && (
          <div className='word-row-english' lang={meaning.lang}>
            {meaning.text}
          </div>
        )}
        {(lastSeenLabel || struggling) && (
          <div className='word-row-meta-line'>
            {lastSeenLabel && (
              <span className='word-row-last-seen' title={lastSeenTitle}>
                <svg
                  className='word-row-last-seen-icon'
                  width='10'
                  height='10'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                  focusable='false'
                >
                  <circle cx='12' cy='12' r='9' />
                  <polyline points='12 7 12 12 16 14' />
                </svg>
                <span>{t('row.seen', { when: lastSeenLabel })}</span>
              </span>
            )}
            {struggling && <StruggleBadge lookups={item.lookups} />}
          </div>
        )}
      </div>
      <div className='word-row-meta'>
        <span className='word-row-freq' title={t('row.frequencyTitle', { rank: String(item.frequency) })}>
          #{item.frequency}
        </span>
        <div
          className={`word-row-encountered${item.encountered ? '' : ' unseen'}`}
          title={item.encountered ? t('row.encountered') : t('row.notSeen')}
        />
      </div>
      <div className='mastery-control' role='group' aria-label={t('row.masteryFor', { word: displayWord })}>
        {masteryOptions.map((m) => (
          <button
            key={m.value}
            className={item.mastery === m.value ? 'active' : ''}
            onClick={() => handleMastery(m.value)}
            aria-pressed={item.mastery === m.value}
          >
            {t(m.label)}
          </button>
        ))}
      </div>
    </div>
  )
}

export default WordRow
